import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { getTiptapExtensions, tiptapEditorAttributes } from '@/lib/tiptap';
import { EditorToolbar } from './EditorToolbar';
import { cn } from '@/lib/utils';

interface TipTapEditorProps {
  content: string | object;
  onUpdate?: (payload: { html: string; json: any }) => void;
  editable?: boolean;
  className?: string;
  placeholder?: string;
  minimal?: boolean; // For comment mode
  articleTitle?: string; // For export filename
}

export function TipTapEditor({
  content,
  onUpdate,
  editable = true,
  className,
  placeholder,
  minimal = false,
  articleTitle,
}: TipTapEditorProps) {
  const editor = useEditor({
    extensions: getTiptapExtensions(),
    content,
    editable,
    editorProps: {
      attributes: {
        ...tiptapEditorAttributes,
        class: cn(
          tiptapEditorAttributes.class,
          minimal && 'min-h-[120px]',
        ),
      },
    },
    onUpdate: ({ editor }) => {
      if (onUpdate) {
        onUpdate({ html: editor.getHTML(), json: editor.getJSON() });
      }
    },
    onCreate: ({ editor }) => {
      console.log('TipTap editor created with commands:', Object.keys(editor.commands));
    },
  });

  // Update content when prop changes
  React.useEffect(() => {
    if (!editor) return;

    if (typeof content === "object") {
      // Compare JSON objects
      const currentJson = JSON.stringify(editor.getJSON());
      const newJson = JSON.stringify(content);
      if (currentJson !== newJson) {
        editor.commands.setContent(content, false);
      }
    } else {
      // Compare HTML strings
      if (content !== editor.getHTML()) {
        editor.commands.setContent(content, false);
      }
    }
  }, [content, editor]);

  // Update placeholder
  React.useEffect(() => {
    if (editor && placeholder) {
      editor.extensionManager.extensions.forEach((extension) => {
        if (extension.name === 'placeholder') {
          extension.options.placeholder = placeholder;
        }
      });
    }
  }, [editor, placeholder]);

  if (!editor) {
    return (
      <div className={cn("border border-border rounded-lg bg-background", className)}>
        <div className="h-12 border-b border-border animate-pulse bg-muted/50" />
        <div className="p-4 min-h-[200px] animate-pulse">
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="h-4 bg-muted rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("border border-border rounded-lg bg-background overflow-visible", className)}>
      {editable && !minimal && <EditorToolbar editor={editor} articleTitle={articleTitle} />}

      {minimal && editable && (
        <div className="border-b border-border bg-background px-3 py-2 overflow-hidden">
          <div className="flex items-center gap-1">
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().toggleBold().run();
              }}
              className={cn(
                "p-2 rounded hover:bg-muted transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center",
                editor.isActive('bold') && "bg-muted text-foreground"
              )}
              title="Bold"
            >
              <span className="font-bold text-sm">B</span>
            </button>
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().toggleItalic().run();
              }}
              className={cn(
                "p-2 rounded hover:bg-muted transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center",
                editor.isActive('italic') && "bg-muted text-foreground"
              )}
              title="Italic"
            >
              <span className="italic text-sm">I</span>
            </button>
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                const url = window.prompt('Enter link URL:');
                if (url) {
                  editor.chain().focus().setLink({ href: url }).run();
                }
              }}
              className={cn(
                "p-2 rounded hover:bg-muted transition-colors text-xs font-medium min-w-[32px] min-h-[32px] flex items-center justify-center",
                editor.isActive('link') && "bg-muted text-foreground"
              )}
              title="Add Link"
            >
              Link
            </button>
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().toggleTaskList().run();
              }}
              className={cn(
                "p-2 rounded hover:bg-muted transition-colors text-xs font-medium min-w-[32px] min-h-[32px] flex items-center justify-center",
                editor.isActive('taskList') && "bg-muted text-foreground"
              )}
              title="Task List"
            >
              ☑
            </button>
          </div>
        </div>
      )}

      <div className="relative overflow-visible">
        <EditorContent
          editor={editor}
          className={cn(
            "prose-editor overflow-visible",
            minimal ? "p-3" : "p-0",
            !editable && "cursor-default"
          )}
        />

        {editable && editor.isEmpty && (
          <div className="absolute top-4 left-4 text-muted-foreground pointer-events-none">
            {placeholder || (minimal ? "Add a comment..." : "Start writing your article...")}
          </div>
        )}
      </div>
    </div>
  );
}

// CSS styles for the editor - add to globals.css
export const editorStyles = `
.prose-editor {
  max-width: none;
}

.prose-editor .ProseMirror {
  outline: none;
}

.prose-editor h1 {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.prose-editor h2 {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
}

.prose-editor h3 {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.4;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.prose-editor p {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

.prose-editor ul,
.prose-editor ol {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
}

.prose-editor li {
  margin: 0.25rem 0;
}

.prose-editor blockquote {
  border-left: 4px solid hsl(var(--border));
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: hsl(var(--muted-foreground));
}

.prose-editor code {
  background-color: hsl(var(--muted));
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.prose-editor pre {
  background-color: hsl(var(--muted));
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 1rem 0;
}

.prose-editor pre code {
  background: none;
  padding: 0;
}

.prose-editor .task-list {
  list-style: none;
  padding-left: 0;
}

.prose-editor .task-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.prose-editor .task-item > label {
  margin-top: 0.125rem;
}

.prose-editor .task-item > div {
  flex: 1;
}

.prose-editor hr {
  border: none;
  border-top: 1px solid hsl(var(--border));
  margin: 2rem 0;
}

.prose-editor img {
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
  margin: 1rem 0;
}

.prose-editor .mention {
  background-color: hsl(var(--primary) / 0.1);
  color: hsl(var(--primary));
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-weight: 500;
  text-decoration: none;
}
`;